import { atom } from 'jotai';
import { MergedDelegateInfo } from './combinedDataAtom';
import { getTokenBalanceOf, getVotingPower } from '@/app/helpers/contracts/token/read';
import { formatUnits } from 'ethers';
import { abbreviateValue } from '@/app/helpers/abbreviateValue';
import { delegatesAtom } from './delegates';
import { userInfoAtom } from './users';
import { fetchTokenInfoAtom, tokenInfoAtom } from './token';

async function GetVotingPower(account: string): Promise<string> {
  const votingPower = await getVotingPower(account);
  return votingPower;
}
async function getTokenBalance(address: `0x${string}`) {
  try {
    const { balance, decimals } = await getTokenBalanceOf(address);
    const rawBalance = parseFloat(formatUnits(balance, decimals));
    const formattedBalance =
      rawBalance >= 1_000_000
        ? abbreviateValue(rawBalance)
        : new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(rawBalance);

    return formattedBalance;
  } catch (error) {
    console.info('Something went wrong');
  }
}

export const profileCombinedDataAtom = atom(
  async (get) => {
    const delegates = (await get(delegatesAtom)) as MergedDelegateInfo[];
    const users = get(userInfoAtom) as MergedDelegateInfo[];

    let updatedUsers: any = await Promise.all(
      users.map(async (user) => {
        const delegate = delegates.find(
          (delegate) => delegate.address === user.wallet_address,
        );

        let tokenInfo;
        let votingPower;
        let tokenBalance;

        if (user.wallet_address) {
          tokenInfo = get(tokenInfoAtom);
          votingPower = await GetVotingPower(user.wallet_address);
          tokenBalance = await getTokenBalance(user.wallet_address as `0x${string}`);
        }

        return delegate
          ? {
              ...user,
              ...(delegate ? delegate : {}),
              ...tokenInfo,
              votingPower,
              tokenBalance,
            }
          : { ...user, ...tokenInfo, votingPower, tokenBalance };
      }),
    );

    const delegatesToAdd = delegates
      .filter(
        (delegate) =>
          !updatedUsers.some((user: any) => user.wallet_address === delegate.address),
      )
      .map((delegate) => {
        let tokenInfo;
        if (delegate.address) {
          tokenInfo = get(tokenInfoAtom);
        }
        return {
          wallet_address: delegate.address,
          profile_name: undefined,
          delegation_statement: undefined,
          is_seeking_delegation: undefined,
          avatar_url: undefined,
          twitter_handle: undefined,
          twitter_link: undefined,
          bio: undefined,
          votingPower: delegate.votingPower,
          tokenBalance: delegate.tokenBalance,
          delegationsReceived: delegate.delegationsReceived,
          isUserSelfDelegated: delegate.isUserSelfDelegated,
          delegators: delegate.delegators,
          ensAvatar: delegate.ensAvatar,
          address: delegate.address,
          balance: tokenInfo?.balance,
          symbol: tokenInfo?.symbol,
          totalSupply: tokenInfo?.totalSupply,
          circulatingSupply: tokenInfo?.circulatingSupply,
        };
      });

    updatedUsers = [...updatedUsers, ...delegatesToAdd];

    const filteredUsers = updatedUsers.filter(
      (user: any) =>
        (user.delegation_statement === undefined &&
          delegates.some((delegate) => delegate.address === user.wallet_address)) ||
        (user.delegation_statement !== undefined &&
          delegates.some((delegate) => delegate.address === user.wallet_address)) ||
        (user.delegation_statement !== undefined &&
          !delegates.some((delegate) => delegate.address === user.wallet_address)),
    );

    return filteredUsers;
  },
  async (get, set, update) => {
    const users = await get(profileCombinedDataAtom);
    users.forEach((user: any) => {
      if (user.wallet_address) {
        set(fetchTokenInfoAtom, user.wallet_address as `0x${string}`);
      }
    });
  },
);
